import Phaser from 'phaser';
import { DESIGN, COLORS } from '@/config';
import { ALL_ASSETS } from '@/assets/manifest';

/**
 * Preload: turns every art-manifest entry into a Phaser texture — SVGs rasterised
 * at their authored resolution, raster bitmaps (AI art) loaded natively — and
 * shows a progress bar. Also generates a procedural circle as the universal
 * fallback so the renderer still has something to draw if an asset is missing.
 */
export class PreloadScene extends Phaser.Scene {
  constructor() {
    super('preload');
  }

  preload(): void {
    const { width, height } = DESIGN;
    const barW = 420;
    const barX = (width - barW) / 2;
    const barY = height / 2;

    this.add
      .text(width / 2, barY - 48, 'Rogue Defender Champions', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: COLORS.accent,
      })
      .setOrigin(0.5);

    const frame = this.add.graphics();
    frame.lineStyle(2, 0x565d8c, 1).strokeRect(barX, barY, barW, 18);
    const bar = this.add.graphics();
    this.load.on('progress', (p: number) => {
      bar.clear().fillStyle(0x5ac0a0, 1).fillRect(barX + 2, barY + 2, (barW - 4) * p, 14);
    });

    // A missing/garbled asset must NOT take down boot — the renderer already
    // falls back to a vector primitive when a texture key is absent. Log and
    // continue so one bad file degrades gracefully instead of crashing preload.
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`[assets] failed to load "${file.key}" (${file.src}); falling back`);
    });

    // Load each manifest asset: raster bitmaps (AI art) straight via `load.image`,
    // SVGs rasterised to a texture at their authored resolution. `pending` assets
    // are authored out-of-band and may not be on disk yet — skip loading them (a
    // 404 would log a console error) until the file lands and `pending` is cleared;
    // the renderer falls back to the SVG/gradient in the meantime.
    for (const a of ALL_ASSETS) {
      if (a.pending) continue;
      if (a.image) this.load.image(a.key, a.path);
      else this.load.svg(a.key, a.path, { width: a.raster, height: a.rasterH ?? a.raster });
    }
  }

  create(): void {
    // Generate a soft circular texture as the universal fallback (used by the
    // renderer when an entity has no `unit_*`/`enemy_*` token loaded).
    const r = 14;
    const g = this.add.graphics();
    g.fillStyle(COLORS.enemy, 1).fillCircle(r, r, r);
    g.lineStyle(3, 0x3a0f0f, 0.6).strokeCircle(r, r, r);
    g.generateTexture('enemy', r * 2, r * 2);
    g.destroy();

    this.scene.start('menu');
  }
}
